import type { ChartDataset } from "chart.js";
import type { EnrollmentCohort, EnrollmentCohortCurvesResponse } from "@t3tools/contracts";

const COLORS = [
  "#2563eb",
  "#dc2626",
  "#059669",
  "#9333ea",
  "#d97706",
  "#0891b2",
  "#db2777",
  "#4f46e5",
  "#65a30d",
  "#ea580c",
  "#0d9488",
  "#7c3aed",
  "#be123c",
  "#0284c7",
  "#a16207",
  "#475569",
];
const DASHES = [
  [],
  [9, 4],
  [2, 4],
  [12, 4, 2, 4],
  [5, 3],
  [14, 5],
  [10, 3, 2, 3, 2, 3],
  [3, 2],
  [16, 3, 4, 3],
  [7, 2, 2, 2],
  [4, 5],
  [12, 2, 4, 2, 2, 2],
  [1, 3],
  [18, 6],
  [8, 4, 4, 4],
  [6, 2, 1, 2, 1, 2],
];

export function cohortLabel(cohort: EnrollmentCohort): string {
  const month = new Intl.DateTimeFormat("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${cohort.month}T00:00:00Z`));
  return `${month} (n=${cohort.size.toLocaleString("en-US")})`;
}

export function cohortStyle(index: number) {
  const base = DASHES[index % DASHES.length] ?? [];
  // Extend the dash pattern instead of repeating it when more months are added.
  const extra = index < DASHES.length ? [] : [20 + Math.floor(index / DASHES.length), 6];
  return { color: COLORS[index % COLORS.length] ?? COLORS[0]!, dash: [...base, ...extra] };
}

export function cohortDataset(
  cohort: EnrollmentCohort,
  index: number,
): ChartDataset<"line", { x: number; y: number }[]> {
  const { color, dash } = cohortStyle(index);
  return {
    label: cohortLabel(cohort),
    data: cohort.points.map((point) => ({ x: point.day, y: point.percent })),
    borderColor: color,
    backgroundColor: color,
    borderDash: dash,
    borderWidth: 2.5,
    pointRadius: (context) => (context.dataIndex === cohort.points.length - 1 ? 3 : 0),
    pointHoverRadius: 5,
    stepped: "before",
    segment: {
      borderColor: (context) =>
        (context.p0.parsed.x ?? 0) >= cohort.fullyObservedDays ? `${color}66` : color,
    },
  };
}

function record(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
function nonnegative(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value >= 0;
}

export function isEnrollmentCohortCurvesResponse(
  value: unknown,
): value is EnrollmentCohortCurvesResponse {
  return (
    record(value) &&
    value.source === "live" &&
    typeof value.asOf === "string" &&
    Number.isFinite(Date.parse(value.asOf)) &&
    Array.isArray(value.clinics) &&
    value.clinics.every(
      (clinic: unknown) =>
        record(clinic) &&
        typeof clinic.id === "string" &&
        typeof clinic.name === "string" &&
        nonnegative(clinic.undatedCount) &&
        Array.isArray(clinic.cohorts) &&
        clinic.cohorts.every(
          (cohort: unknown) =>
            record(cohort) &&
            typeof cohort.month === "string" &&
            /^\d{4}-(0[1-9]|1[0-2])-01$/.test(cohort.month) &&
            nonnegative(cohort.size) &&
            cohort.size > 0 &&
            nonnegative(cohort.converted) &&
            cohort.converted <= cohort.size &&
            nonnegative(cohort.espoMatched) &&
            nonnegative(cohort.fullyObservedDays) &&
            nonnegative(cohort.oldestFollowupDays) &&
            cohort.fullyObservedDays <= cohort.oldestFollowupDays &&
            Array.isArray(cohort.points) &&
            cohort.points.length > 0 &&
            cohort.points.every(
              (point: unknown) =>
                record(point) &&
                nonnegative(point.day) &&
                nonnegative(point.enrolled) &&
                point.enrolled <= Number(cohort.size) &&
                nonnegative(point.percent) &&
                point.percent <= 100,
            ),
        ),
    )
  );
}
